import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  SearchIcon,
  ArrowDownBlackIcon,
  MenuVerticalIcon,
  EditIcon,
  VisibilityIcon,
} from "~/assets/icons";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { notify } from "~/utils/helper";
import CreateMaterialOrderForm, { MaterialOrder } from "./CreateMaterialOrderForm";
import MaterialOrderDetailView from "./MaterialOrderDetailView";
import jobDetailsApi from "~/apis/jobDetails";

interface MaterialOrderTabProps {
  jobId?: string;
  onDetailsOpenChange?: (isOpen: boolean) => void;
  onRegisterBackFromDetails?: (back: (() => void) | null) => void;
}

const MaterialOrderTab = ({ jobId: jobIdProp, onDetailsOpenChange, onRegisterBackFromDetails }: MaterialOrderTabProps) => {
  const jobId = jobIdProp ?? (window.location.pathname.split("/").pop() || "");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("All Orders");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [materialOrders, setMaterialOrders] = useState<MaterialOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [editOrderId, setEditOrderId] = useState<string | null>(null);
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const filterOptions = ["All Orders", "Pending", "In Progress", "Completed"];

  // Format date from ISO string
  const formatDate = (dateString: string): { date: string; time: string } => {
    const date = new Date(dateString);
    const dateStr = date.toISOString().split("T")[0];
    const timeStr = date.toTimeString().split(" ")[0].slice(0, 5);
    return { date: dateStr, time: timeStr };
  };

  // Format currency
  const formatCurrency = (price: string): string => {
    const numPrice = parseFloat(price);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(numPrice);
  };

  // Convert API response to MaterialOrder format
  const convertToMaterialOrder = (order: any): MaterialOrder => {
    const { date, time } = formatDate(order.date);
    return {
      id: order.id.toString(),
      materialOrderId: order.materialOrderId,
      date: date,
      time: time,
      internalNote: order.internalNotes || "",
      total: formatCurrency(order.totalPrice),
      status: order.status as "Pending" | "In Progress" | "Completed",
    };
  };

  // Load material orders from API
  const loadMaterialOrders = async () => {
    if (!jobId) return;

    try {
      setIsLoading(true);
      const response = await jobDetailsApi.getMaterialOrders(jobId);
      if (response.data.status === "success" && response.data.data) {
        const convertedOrders = response.data.data.map(convertToMaterialOrder);
        setMaterialOrders(convertedOrders);
      }
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to load material orders");
    } finally {
      setIsLoading(false);
    }
  };

  // Load material orders on mount
  useEffect(() => {
    if (jobId && !showCreateForm) {
      loadMaterialOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, showCreateForm]);

  // Filter and search material orders
  const filteredMaterialOrders = useMemo(() => {
    let filtered = [...materialOrders];

    // Filter by status
    if (selectedFilter !== "All Orders") {
      filtered = filtered.filter((order) => order.status === selectedFilter);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          order.materialOrderId.toLowerCase().includes(query) ||
          order.internalNote.toLowerCase().includes(query) ||
          order.total.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [materialOrders, searchQuery, selectedFilter]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId && menuRefs.current[openMenuId]) {
        if (!menuRefs.current[openMenuId]?.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleAddMaterialOrder = () => {
    setShowCreateForm(true);
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setEditOrderId(null);
  };

  const handleSubmitCreate = () => {
    setShowCreateForm(false);
    setEditOrderId(null);
    // Reload material orders after creating
    loadMaterialOrders();
  };

  const handleView = (order: MaterialOrder) => {
    setOpenMenuId(null);
    setSelectedOrderId(order.id);
  };

  const handleBackFromDetail = () => {
    setSelectedOrderId(null);
  };

  const isDetailsOpen = !!selectedOrderId;

  useEffect(() => {
    onDetailsOpenChange?.(isDetailsOpen);
  }, [isDetailsOpen, onDetailsOpenChange]);

  useEffect(() => {
    if (isDetailsOpen) {
      onRegisterBackFromDetails?.(() => {
        setSelectedOrderId(null);
      });
    } else {
      onRegisterBackFromDetails?.(null);
    }
    return () => onRegisterBackFromDetails?.(null);
  }, [isDetailsOpen, onRegisterBackFromDetails]);

  const handleEditFromDetail = (orderId: string) => {
    setSelectedOrderId(null);
    setEditOrderId(orderId);
    setShowCreateForm(true);
  };

  const handleEdit = (order: MaterialOrder) => {
    setOpenMenuId(null);
    // Only allow editing draft orders
    if (order.status !== "Draft") {
      notify("warning", "Cannot edit submitted order. Only draft orders can be edited.");
      return;
    }
    setEditOrderId(order.id);
    setShowCreateForm(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "In Progress":
        return "bg-[#FF9500] text-white";
      case "Completed":
        return "bg-[#10B981] text-white";
      case "Pending":
        return "bg-[#6B7280] text-white";
      default:
        return "bg-[#6B7280] text-white";
    }
  };

  // Render Detail View
  if (selectedOrderId) {
    return (
      <MaterialOrderDetailView
        jobId={jobId || ""}
        materialOrderId={selectedOrderId}
        onBack={handleBackFromDetail}
        onEdit={handleEditFromDetail}
      />
    );
  }

  // Render Create Form
  if (showCreateForm) {
    return (
      <CreateMaterialOrderForm
        onCancel={handleCancelCreate}
        onSubmit={handleSubmitCreate}
        previousOrders={materialOrders}
        jobId={jobId || ""}
        editOrderId={editOrderId || undefined}
      />
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-[#071427] text-white">
      {/* Top Bar */}
      <div className="flex items-center gap-4 p-4 border-b border-[#111e33]">
        {/* Search Bar */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#A2A7B4]" />
          <input
            type="text"
            placeholder="Search Material Order"
            value={searchQuery}
            onChange={handleSearch}
            className="w-[239px] h-[40px] pl-10 pr-4 bg-[#0C192D] border border-[#1f2937] rounded-lg text-white placeholder-[#A2A7B4] focus:outline-none focus:border-[#3b82f6]"
          />
        </div>

        {/* Filter Dropdown */}
        <div className="flex-1  relative">
          <button
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className="flex items-center gap-2 px-4 py-2 bg-[#D9D9D9] text-[#000000] text-[14px] rounded-[27px] hover:bg-[#C9C9C9] transition-colors"
          >
            <span className="text-[14px] text-[#000000]">{selectedFilter}</span>
            <ArrowDownBlackIcon className="w-2 h-1" />
          </button>
          {showFilterDropdown && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowFilterDropdown(false)}
              />
              <div className="absolute top-full mt-2 right-0 z-20 bg-[#0C192D] border border-[#1f2937] rounded-lg shadow-lg min-w-[150px]">
                {filterOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => {
                      setSelectedFilter(option);
                      setShowFilterDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2 hover:bg-[#1f2937] transition-colors ${selectedFilter === option
                      ? "bg-[#1f2937] text-[#3b82f6]"
                      : "text-white"
                      }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Add Material Order Button */}
        <PrimaryButton
          title="Add Material Order"
          onClick={handleAddMaterialOrder}
          className="!bg-[#3b82f6] !text-white hover:!bg-[#2563eb] !rounded-[27px]"
        />
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-[10px]">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-[#A2A7B4]">Loading material orders...</p>
          </div>
        ) : (
          <div className="bg-[#0E264C] rounded-[12px] overflow-visible">
            <table className="w-full">
              <thead className="bg-[#0E264C] sticky top-0 z-10">
                <tr>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe] pl-5">
                    Material Order ID
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe]">
                    Date
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe]">
                    Internal Note
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe]">
                    Total
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe]">
                    Status
                  </th>
                  <th className="px-4 h-[72px] text-left text-base font-semibold text-[#f9fbfe] pr-5">
                  </th>
                </tr>
                <tr>
                  <td colSpan={6} className="px-0">
                    <div className="mx-5 border-b border-[#111e33]"></div>
                  </td>
                </tr>
              </thead>
              <tbody>
                {filteredMaterialOrders.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-[#A2A7B4]">
                      No material orders found
                    </td>
                  </tr>
                ) : (
                  filteredMaterialOrders.map((order) => (
                    <React.Fragment key={order.id}>
                      <tr className="hover:bg-[#0a1b34] transition-colors">
                        <td className="px-4 py-4 pl-5">
                          <span className="text-sm text-white">{order.materialOrderId}</span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col">
                            <span className="text-sm text-white">{order.date}</span>
                            <span className="text-xs text-[#A2A7B4]">{order.time}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-[#949699]">{order.internalNote || "-"}</span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-[#949699] whitespace-nowrap">
                            {order.total}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                              order.status
                            )}`}
                          >
                            {order.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 pr-5">
                          <div className="relative" ref={(el) => (menuRefs.current[order.id] = el)}>
                            <button
                              onClick={() =>
                                setOpenMenuId(openMenuId === order.id ? null : order.id)
                              }
                              className="p-2 hover:bg-[#1f2937] rounded transition-colors"
                            >
                              <MenuVerticalIcon className="w-5 h-5 text-[#A2A7B4]" />
                            </button>
                            {openMenuId === order.id && (
                              <div className="absolute right-0 top-full mt-2 z-50 bg-[#0C192D] border border-[#1f2937] rounded-lg shadow-lg w-[229px] overflow-hidden">
                                <button
                                  onClick={() => handleView(order)}
                                  className="w-full px-4 py-2 hover:bg-[#1f2937] transition-colors text-white flex items-center justify-center gap-2"
                                >
                                  <span>View</span>
                                </button>
                                <div className="border-b border-[#111E33]"></div>
                                <button
                                  onClick={() => handleEdit(order)}
                                  className="w-full px-4 py-2 hover:bg-[#1f2937] transition-colors text-white flex items-center justify-center gap-2"
                                >
                                  <span>Edit</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={6} className="px-0 py-0">
                          <div className="mx-5 border-b border-[#111e33]"></div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialOrderTab;
